/**
 * The business day, as the browser sees it.
 *
 * Every date input in this app is a *business day* — the day a sale, purchase,
 * payment or delivery belongs to — not an instant. The server derives those in
 * `config('app.timezone')`, which is the timezone the business trades in.
 *
 * The client used to derive them with `new Date().toISOString().slice(0, 10)`.
 * `toISOString()` is always UTC, so at UTC+5 every date typed between midnight
 * and 05:00 local defaulted to *yesterday* — the previous day's rollup, and at a
 * month boundary the previous FBR period.
 *
 * These read the browser's own zone, which for a single-country deployment is
 * the trading timezone, so client and server agree. A user working from another
 * country would default to their own day; the date is an editable field on every
 * screen that uses it, and the server owns what is finally stored.
 */

/** Zero-pad to two digits without pulling in a date library. */
const pad = (value) => String(value).padStart(2, '0');

/**
 * `YYYY-MM-DD` for a date, in local time.
 * @param {Date} [date] defaults to now
 */
export const toLocalDateString = (date = new Date()) => {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return '';

    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
};

/** Today's business day as `YYYY-MM-DD`. The replacement for `toISOString().slice(0, 10)`. */
export const todayLocal = () => toLocalDateString(new Date());

/**
 * `YYYY-MM-DDTHH:mm` in local time, for `datetime-local` inputs.
 * `toISOString().slice(0, 16)` is wrong here for the same reason.
 */
export const nowLocalDateTime = (date = new Date()) => {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return '';

    return `${toLocalDateString(value)}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
};

export default todayLocal;
